import { useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { statisticsClient } from '../../api'
import type { ClosedPositionSearchItem, PositionPage } from '../../api/statisticsTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { formatDateTime } from '../trading/tradingConstants'
import { coinSheetLink, pairLabel } from './positionLinks'
import { useCursorPage } from './useCursorPage'
import { failureText } from '../../api/errorMessages'

function num(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/** Legacy title/description per search bucket
 * (`StatisticsClosedPositionsSearchComponent`). The backend's actual type
 * name is `off-handler` (hyphen) — legacy's own title check looked for
 * `offhandler` and so never matched it, a defect kept here only as a code
 * comment, not reproduced (this page shows the correct description for
 * whichever `:type` is actually in the URL). */
function suffixFor(type: string): { title: string; description: string } | null {
  const key = type.toLowerCase()
  if (key === 'withloss' || key === 'with-loss') {
    return { title: 'only loss', description: 'Positions closed with loss' }
  }
  if (key === 'off-handler' || key === 'offhandler') {
    return { title: 'off handler', description: 'Positions closed because of Coin Off Mode Handler' }
  }
  if (key === 'manual') {
    return { title: 'manual trading', description: 'Positions closed by manual trading (including LightBot methods)' }
  }
  return null
}

/**
 * Equivalent of legacy `StatisticsClosedPositionsSearchComponent` (route
 * `.../statistics/positions/closed/:type`, contract #12, BigQuery-backed).
 * Same columns as `ClosedPositionsPage`, but the Profit column is `profit`
 * (sellAmount − buyAmount), not `yield`.
 */
export function ClosedPositionsSearchPage() {
  const { exchangeId = '', tradingType = 'spot', type = 'plain' } = useParams<{
    exchangeId: string
    tradingType: string
    type: string
  }>()
  if (tradingType !== 'spot') {
    return (
      <div className="portal-card">
        <h1>Closed positions search</h1>
        <p className="portal-notice portal-notice--warning">Margin trading is not supported.</p>
      </div>
    )
  }
  return <SpotClosedPositionsSearch key={`${exchangeId}:${type}`} exchangeId={exchangeId} type={type} />
}

function SpotClosedPositionsSearch({ exchangeId, type }: { exchangeId: string; type: string }) {
  const fetcher = useCallback(
    (pageSize: number, start: string | undefined) =>
      statisticsClient.closedPositionSearch(exchangeId, type, { pageSize, start }) as Promise<
        PositionPage<ClosedPositionSearchItem>
      >,
    [exchangeId, type],
  )
  const { items, loading, error, page, hasPrev, hasNext, next, prev, pageSize } = useCursorPage<ClosedPositionSearchItem>(
    fetcher,
    [exchangeId, type],
  )
  const suffix = suffixFor(type)

  const columns: Column<ClosedPositionSearchItem>[] = [
    { key: 'openedAt', label: 'Open Date', align: 'left', sortValue: (r) => r.openedAt, format: (v) => formatDateTime(v as string) },
    { key: 'closedAt', label: 'Close Date', align: 'left', sortValue: (r) => r.closedAt, format: (v) => formatDateTime(v as string) },
    {
      key: 'coinPair',
      label: 'Coin pair',
      align: 'left',
      sortValue: (r) => pairLabel(r.coin1, r.coin2),
      format: (_v, r) => <Link to={coinSheetLink(exchangeId, r.coin1, r.coin2)}>{r.coinPair ?? pairLabel(r.coin1, r.coin2)}</Link>,
    },
    { key: 'buyPrice', label: 'Buy Price', numeric: true, sortValue: (r) => num(r.buyPrice), format: (v) => num(v).toFixed(8) },
    {
      key: 'buyAmount',
      label: 'Buy Value (quote)',
      numeric: true,
      sortValue: (r) => num(r.buyAmount),
      format: (_v, r) => `${formatNumberByCoin(num(r.buyAmount), r.coin2)} ${r.coin2}`,
    },
    { key: 'sellPrice', label: 'Sell Price', numeric: true, sortValue: (r) => num(r.sellPrice), format: (v) => num(v).toFixed(8) },
    {
      key: 'sellAmount',
      label: 'Sell Value (quote)',
      numeric: true,
      sortValue: (r) => num(r.sellAmount),
      format: (_v, r) => `${formatNumberByCoin(num(r.sellAmount), r.coin2)} ${r.coin2}`,
    },
    {
      key: 'profit',
      label: 'Profit',
      numeric: true,
      sortValue: (r) => num(r.profit),
      cellClass: (_v, r) => (num(r.profit) < 0 ? 'portal-pnl-negative' : num(r.profit) > 0 ? 'portal-pnl-positive' : undefined),
      format: (_v, r) => `${formatNumberByCoin(num(r.profit), r.coin2)} ${r.coin2}`,
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Last Closed Positions{suffix ? ` - ${suffix.title}` : ''}</h1>
      </div>
      <div className="portal-card">
        {suffix && <p className="portal-text-muted">{suffix.description}</p>}
        {loading && <p>Loading…</p>}
        {error !== null && <p role="alert">{failureText(error, 'Could not load closed positions')}</p>}
        {!loading && !error && items.length === 0 && <p>No Closed Positions</p>}
        {items.length > 0 && (
          <div className="portal-table-scroll">
            <Table columns={columns} rows={items} rowKey={(r) => r.id} />
          </div>
        )}
        <nav className="gbx-pager" aria-label="Pagination">
          <button type="button" disabled={!hasPrev} onClick={prev}>
            Previous
          </button>
          <span>
            Page {page} ({pageSize}/page)
          </span>
          <button type="button" disabled={!hasNext} onClick={next}>
            Next
          </button>
        </nav>
      </div>
    </div>
  )
}
